import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { MAIN_SITE_URL } from "@/mocks/categories";

const languages = [
  { code: "en", label: "EN", name: "English" },
  { code: "pl", label: "PL", name: "Polski" },
  { code: "es", label: "ES", name: "Español" },
  { code: "fr", label: "FR", name: "Français" },
];

const guideLinks = [
  { slug: "usfans-spreadsheet-guide", labelKey: "nav.guide" },
  { slug: "usfans-review", labelKey: "nav.review" },
  { slug: "usfans-shipping-cost-guide", labelKey: "nav.shipping" },
  { slug: "usfans-qc-guide", labelKey: "nav.qc" },
  { slug: "usfans-coupon-guide", labelKey: "nav.coupon" },
  { slug: "usfans-warehouse-guide", labelKey: "nav.warehouse" },
  { slug: "is-usfans-legit", labelKey: "nav.legit" },
];

export default function SiteHeader() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [langOpen, setLangOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const langRef = useRef<HTMLDivElement | null>(null);
  const guideRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMobileOpen(false);
    setMobileGuideOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-background-50 border-b border-background-200">
        <div className="flex items-center justify-between h-16 px-4 md:px-8 max-w-[1600px] mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
            <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading font-bold text-lg">
              U
            </span>
            <span className="font-heading font-bold text-foreground-900 text-lg leading-none hidden sm:block">
              {t("brand")}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 rounded-md text-sm font-semibold text-foreground-800 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("nav.home")}
            </Link>

            {/* Shopping guide dropdown */}
            <div className="relative" ref={guideRef}>
              <button
                onClick={() => setGuideOpen((v) => !v)}
                className="flex items-center gap-1 px-4 py-2 rounded-md text-sm font-semibold text-foreground-800 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("nav.guides")}
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${guideOpen ? "rotate-180" : ""}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute left-0 top-full mt-1 w-64 rounded-lg bg-background-50 border border-background-200 p-1 z-50">
                  {guideLinks.map((g) => (
                    <Link
                      key={g.slug}
                      to={`/guides/${g.slug}`}
                      onClick={() => setGuideOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-article-line text-primary-500 shrink-0"></i>
                      {t(g.labelKey)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Language selector */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                aria-label={t("language")}
              >
                <i className="ri-global-line text-base text-primary-500"></i>
                <span className="font-semibold">{languages.find((l) => l.code === i18n.language)?.label ?? "EN"}</span>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 w-36 rounded-lg bg-background-50 border border-background-200 p-1 z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => changeLanguage(l.code)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm cursor-pointer transition-colors whitespace-nowrap flex items-center gap-2 ${
                        i18n.language === l.code
                          ? "bg-primary-100 text-primary-700 font-semibold"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <span className="text-xs font-bold w-7 text-primary-600">{l.label}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line text-base"></i>
              {t("buyNow")}
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Open menu"
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-background-50/95 backdrop-blur-sm overflow-y-auto">
          <div className="pt-20 pb-10 px-5">
            {/* Home */}
            <Link
              to="/"
              className="flex items-center gap-3 py-3.5 border-b border-background-200 text-base font-semibold text-foreground-900 cursor-pointer"
            >
              <i className="ri-home-4-line text-primary-500 text-lg w-6 h-6 flex items-center justify-center"></i>
              {t("nav.home")}
            </Link>

            {/* Shopping Guides accordion */}
            <button
              onClick={() => setMobileGuideOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-3 py-3.5 border-b border-background-200 text-base font-semibold text-foreground-900 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-book-open-line text-primary-500 text-lg w-6 h-6 flex items-center justify-center"></i>
                {t("nav.guides")}
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${mobileGuideOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileGuideOpen && (
              <div className="pl-9 pb-2">
                {guideLinks.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/guides/${g.slug}`}
                    className="flex items-center gap-2 py-2.5 text-sm text-foreground-700 border-b border-background-100 last:border-0 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-primary-400"></i>
                    {t(g.labelKey)}
                  </Link>
                ))}
              </div>
            )}

            {/* Language selector mobile */}
            <div className="py-3.5 border-b border-background-200">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
                {t("language")}
              </p>
              <div className="flex gap-2 flex-wrap">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => {
                      changeLanguage(l.code);
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                      i18n.language === l.code
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700"
                    }`}
                  >
                    {l.label} · {l.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Buy button mobile */}
            <div className="mt-6">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full py-4 rounded-lg bg-primary-500 text-background-50 font-bold text-base cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-bag-3-line text-lg"></i>
                {t("buyNow")} — USFans Spreadsheet
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}